<template>
  <div
    v-if="isForbidden"
    class="text-xs font-medium text-gpx_gray-200"
    data-testid="command-forbidden"
  >
    {{ FORBIDDEN_MESSAGE }}
  </div>
  <div v-else class="flex flex-col gap-2">
    <!-- Main button (Execute/Undo) -->
    <button
      role="button"
      :class="buttonClasses"
      :disabled="isLoading || commandStatus === 'reverted'"
      @click.prevent="handleCommandAction"
    >
      <div v-if="isLoading" class="flex items-center justify-center gap-2">
        <div class="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
        <span class="font-medium">{{ loadingText }}</span>
      </div>
      <span v-else>{{ buttonText }}</span>
    </button>

    <!-- Command status -->
    <div v-if="commandStatus" class="text-xs text-gpx_gray-200 text-center font-medium">
      {{ statusText }}
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { useStore } from 'vuex';
import * as events from '@/events';
import { getAiCommand, executeCommand, revertCommand } from '@gpx-spa/queries';

const FORBIDDEN_MESSAGE =
  "You don't have the necessary permissions for this action.";

const toast = useToast();
const store = useStore();

const props = defineProps({
  action: {
    type: Object,
    required: true,
  },
});

// Determine if command is revertable
const isRevertable = computed(() => {
  return props.action.name.includes('revertable');
});

// Get command ID from toolResult
const commandId = computed(() => {
  return props.action.toolResult?.command_id;
});

// Get account ID
const accountId = computed(() => {
  return props.action.account_id || store.getters['auth/accountId'];
});

// Command status query - using reactive values
const { data: commandData, isLoading: isCommandLoading, error: commandError } = getAiCommand(
  accountId,
  commandId
);

const isForbiddenError = (error) => error?.response?.status === 403;

// Set when execute/undo is refused even though reading the command was allowed.
const mutationForbidden = ref(false);

const isForbidden = computed(() => {
  return isForbiddenError(commandError.value) || mutationForbidden.value;
});

// Command mutations
const executeCommandMutation = executeCommand();
const revertCommandMutation = revertCommand();

// Button states
const isExecuting = ref(false);
const isReverting = ref(false);

// Computed properties for UI
const commandStatus = computed(() => {
  return commandData.value?.status || 'pending';
});

// Undo also runs as `executing`; only a command that already completed once has executed_at.
const isRevertRunning = computed(() => {
  return commandStatus.value === 'executing' && !!commandData.value?.executed_at;
});

const isLoading = computed(() => {
  return isCommandLoading.value || isExecuting.value || isReverting.value || commandStatus.value === 'executing';
});

const isExecuted = computed(() => {
  return commandStatus.value === 'completed';
});

const buttonText = computed(() => {
  // Show Undo only if command is revertable and completed
  if (isRevertable.value && isExecuted.value) {
    return `Undo ${label.value}`;
  }
  return label.value;
});

const loadingText = computed(() => {
  if (isReverting.value || isRevertRunning.value) return 'Reverting...';
  if (isExecuting.value || commandStatus.value === 'executing') return 'Executing...';
  return 'Loading...';
});

const statusText = computed(() => {
  switch (commandStatus.value) {
    case 'pending':
      return 'Ready to execute';
    case 'executing':
      return isRevertRunning.value ? 'Command is reverting...' : 'Command is executing...';
    case 'completed':
      return isRevertable.value ? 'Command completed - can be undone' : 'Command completed';
    case 'reverted':
      return 'Command was reverted';
    case 'failed':
      return commandData.value?.error ? `Command failed: ${commandData.value.error}` : 'Command failed';
    default:
      return 'Unknown status';
  }
});

const buttonClasses = computed(() => {
  const baseClasses = "w-[150px] inline-flex items-center justify-center rounded-[6px] text-gpx_button font-medium px-3 py-1 transition-all duration-200";

  if (isLoading.value) {
    return `${baseClasses} bg-blue-500 text-white cursor-not-allowed opacity-75`;
  }

  // Disabled button for reverted commands
  if (commandStatus.value === 'reverted') {
    return `${baseClasses} bg-gpx_gray-700 text-white cursor-not-allowed`;
  }

  // Orange button only for revertable commands in completed state
  if (isRevertable.value && isExecuted.value) {
    return `${baseClasses} bg-orange-500 hover:bg-orange-600 text-white`;
  }

  return `${baseClasses} bg-gpx_terracotta-300 hover:bg-gpx_terracotta-200 text-white`;
});

const label = computed(() => {
  return props.action.arguments?.button_text || props.action.name;
});

watch(
  () => commandData.value?.status,
  (status, previous) => {
    if (!previous || status === previous) return;

    const event = { commandId: commandId.value, action: props.action, result: commandData.value };
    if (status === 'completed') {
      toast.success('Command executed successfully!');
      events.trigger(events.names.COMMAND_EXECUTED, event);
    } else if (status === 'reverted') {
      toast.success('Command reverted successfully!');
      events.trigger(events.names.COMMAND_REVERTED, event);
    } else if (status === 'failed') {
      toast.error(statusText.value);
    }
  },
);

// A gateway timeout or dropped connection says nothing about the command, which keeps running on the engine.
const isUnknownOutcome = (error) => !error?.response || error.response.status >= 500;

const handleMutationError = (error, fallbackMessage) => {
  if (isForbiddenError(error)) {
    mutationForbidden.value = true;
    return;
  }
  if (isUnknownOutcome(error)) {
    return;
  }
  toast.error(error.response?.data?.message || fallbackMessage);
};

// Handle command actions
const handleCommandAction = async () => {
  if (!commandId.value) {
    toast.error('Command ID not found');
    return;
  }

  // Prevent multiple clicks during loading
  if (isLoading.value) {
    toast.warning('Command is already being processed, please wait...');
    return;
  }

  // Don't allow actions on reverted commands
  if (commandStatus.value === 'reverted') {
    toast.warning('This command has been reverted and cannot be executed again');
    return;
  }

  // If command is revertable and completed - do revert
  if (isRevertable.value && isExecuted.value) {
    await handleRevert();
  } else {
    // Otherwise execute command
    await handleExecute();
  }
};

const handleExecute = async () => {
  // Prevent multiple executions
  if (isExecuting.value || isReverting.value) {
    return;
  }

  isExecuting.value = true;

  try {
    executeCommandMutation.mutate({
      commandId: commandId.value,
      accountId: accountId.value
    }, {
      onError: (error) => {
        console.error('Error executing command:', error);
        handleMutationError(error, 'Failed to execute command');
      },
      onSettled: () => {
        // Reset executing state when mutation is complete (success or error)
        isExecuting.value = false;
      }
    });
  } catch (error) {
    console.error('Error executing command:', error);
    toast.error('Failed to execute command');
    isExecuting.value = false;
  }
};

const handleRevert = async () => {
  // Check that command is actually revertable
  if (!isRevertable.value) {
    console.warn('Attempted to revert non-revertable command');
    return;
  }

  // Prevent multiple executions
  if (isExecuting.value || isReverting.value) {
    return;
  }

  isReverting.value = true;

  try {
    revertCommandMutation.mutate({
      commandId: commandId.value,
      accountId: accountId.value
    }, {
      onError: (error) => {
        console.error('Error reverting command:', error);
        handleMutationError(error, 'Failed to revert command');
      },
      onSettled: () => {
        // Reset reverting state when mutation is complete (success or error)
        isReverting.value = false;
      }
    });
  } catch (error) {
    console.error('Error reverting command:', error);
    toast.error('Failed to revert command');
    isReverting.value = false;
  }
};
</script>
